/* Bilingual labels + plain-language copy for the time & biometric module. */
import type { BioProtocol, BioVendor, DayStatus, Device, DeviceCommand, PunchDirection, VerifyMode } from "../../../data/types";

export type Lx = (en: string, ar: string) => string;

const DAY: Record<DayStatus, [string, string]> = {
  Present: ["Present", "حاضر"],
  Late: ["Late", "متأخر"],
  Absent: ["Absent", "غائب"],
  "On leave": ["On leave", "في إجازة"],
  Weekend: ["Weekend", "عطلة"],
  "Missing punch": ["Missing punch", "بصمة ناقصة"],
  "Not in yet": ["Not in yet", "لم يحضر بعد"],
};
export const DAY_STATUSES = Object.keys(DAY) as DayStatus[];
export const dayStatusLabel = (s: DayStatus, L: Lx) => L(DAY[s][0], DAY[s][1]);

/** Source column: device name, "Mobile · geofence" or "Manual" (the API sends English keys). */
export function sourceLabel(src: string, L: Lx): string {
  if (!src) return "—";
  if (src === "Mobile · geofence") return L("Mobile · geofence", "الجوال · نطاق جغرافي");
  if (src === "Manual") return L("Manual", "يدوي");
  return src;
}

const PROTO: Record<BioProtocol, [string, string]> = {
  push: ["Cloud push", "دفع سحابي"],
  webhook: ["Webhook", "رابط Webhook"],
  poll: ["Server sync (API)", "مزامنة عبر الخادم (API)"],
  agent: ["StartHR Agent", "وكيل ستارت HR"],
  file: ["File upload", "رفع ملف"],
};
export const protocolLabel = (p: BioProtocol, L: Lx) => L(PROTO[p][0], PROTO[p][1]);

const PROTO_DESC: Record<BioProtocol, [string, string]> = {
  push: ["The device sends punches to StartHR by itself.", "يرسل الجهاز البصمات إلى ستارت HR تلقائيًا."],
  webhook: ["The device posts each event to a private link.", "يرسل الجهاز كل حركة إلى رابط خاص بك."],
  poll: ["StartHR fetches punches from your attendance server every few minutes.", "يجلب ستارت HR البصمات من خادم الحضور لديك كل بضع دقائق."],
  agent: ["A small program on an office PC reads the devices on your network.", "برنامج صغير على حاسب في المكتب يقرأ الأجهزة في شبكتك المحلية."],
  file: ["Export from the device software and upload a CSV/Excel file.", "صدّر السجلات من برنامج الجهاز ثم ارفع ملف CSV أو Excel."],
};
export const protocolDesc = (p: BioProtocol, L: Lx) => L(PROTO_DESC[p][0], PROTO_DESC[p][1]);

export function protocolNeeds(p: BioProtocol, v: BioVendor, L: Lx): string {
  switch (p) {
    case "push": return L("Device has internet", "اتصال الجهاز بالإنترنت");
    case "webhook": return v === "generic"
      ? L("A device or script that can send HTTPS requests", "جهاز أو برنامج يستطيع إرسال طلبات HTTPS")
      : L("Device has internet + access to its web settings", "اتصال الجهاز بالإنترنت + الدخول إلى إعداداته");
    case "poll":
      if (v === "zkbiotime") return L("Your BioTime server URL & an API user", "عنوان خادم BioTime ومستخدم API");
      if (v === "suprema") return L("Your BioStar server URL & login", "عنوان خادم BioStar وبيانات الدخول");
      if (v === "anviz") return L("CrossChex Cloud API key & secret", "مفتاح وسرّ واجهة CrossChex Cloud");
      return L("Device URL reachable from the internet & admin login", "عنوان الجهاز متاح عبر الإنترنت وبيانات المسؤول");
    case "agent": return L("An office PC that stays on", "حاسب في المكتب يبقى يعمل");
    case "file": return L("The device software (ZKTime, iVMS-4200, SmartPSS…)", "برنامج الجهاز (ZKTime أو iVMS-4200 أو SmartPSS…)");
  }
}

/** One protocol per vendor is marked "Recommended" in the wizard. */
export const RECOMMENDED: Record<BioVendor, BioProtocol> = {
  zkteco: "push", hikvision: "webhook", suprema: "webhook", dahua: "webhook", zkbiotime: "poll", anviz: "poll", agent: "agent", generic: "webhook",
};

/** Where to find the serial number (wizard hint). */
export function serialHint(v: BioVendor, p: BioProtocol, L: Lx): string {
  if (p === "agent") return L("Any unique name for this office PC, e.g. JED-PC-01", "أي اسم فريد لحاسب المكتب، مثل JED-PC-01");
  if (v === "zkteco") return L("On the device: Menu → System Info → Device Info, or the sticker on the back", "في الجهاز: القائمة ← معلومات النظام ← معلومات الجهاز، أو الملصق خلف الجهاز");
  if (v === "hikvision") return L("Device web page → System → Device Information, or the sticker on the back", "صفحة الجهاز ← النظام ← معلومات الجهاز، أو الملصق خلف الجهاز");
  if (v === "dahua") return L("Device web page → System Info → Version, or the sticker on the back", "صفحة الجهاز ← معلومات النظام ← الإصدار، أو الملصق خلف الجهاز");
  if (v === "zkbiotime" || v === "suprema" || v === "anviz") return L("Your server's licence/serial number, or any unique ID", "رقم ترخيص الخادم أو أي معرّف فريد");
  return L("The sticker on the back of the device", "الملصق الموجود خلف الجهاز");
}

export const DEVICE_STATUS: Record<Device["status"], [string, string]> = {
  online: ["Online", "متصل"], offline: ["Offline", "غير متصل"], pending: ["Waiting for claim", "بانتظار الاعتماد"],
};

const CMD: Record<DeviceCommand["kind"], [string, string]> = {
  sync_user: ["Sync user", "مزامنة مستخدم"], delete_user: ["Delete user", "حذف مستخدم"], reboot: ["Reboot", "إعادة تشغيل"],
  query_logs: ["Query logs", "طلب السجلات"], info: ["Get info", "جلب المعلومات"], set_time: ["Set time", "ضبط الوقت"], clear_logs: ["Clear logs", "مسح السجلات"],
};
export const commandLabel = (k: DeviceCommand["kind"], L: Lx) => L(CMD[k][0], CMD[k][1]);
const CMD_ST: Record<DeviceCommand["status"], [string, string]> = { queued: ["Queued", "في الانتظار"], sent: ["Sent", "أُرسل"], done: ["Done", "تم"], failed: ["Failed", "فشل"] };
export const commandStatusLabel = (s: DeviceCommand["status"], L: Lx) => L(CMD_ST[s][0], CMD_ST[s][1]);

const DIR: Record<PunchDirection, [string, string]> = {
  in: ["In", "دخول"], out: ["Out", "خروج"], break_out: ["Break out", "بداية استراحة"], break_in: ["Break in", "نهاية استراحة"],
  ot_in: ["OT in", "دخول إضافي"], ot_out: ["OT out", "خروج إضافي"], unknown: ["—", "—"],
};
export const directionLabel = (d: PunchDirection, L: Lx) => L(DIR[d][0], DIR[d][1]);
const VER: Record<VerifyMode, [string, string]> = {
  fingerprint: ["Fingerprint", "بصمة الإصبع"], face: ["Face", "الوجه"], card: ["Card", "بطاقة"], pin: ["PIN", "رمز"], palm: ["Palm", "راحة اليد"], iris: ["Iris", "القزحية"], unknown: ["—", "—"],
};
export const verifyLabel = (v: VerifyMode, L: Lx) => L(VER[v][0], VER[v][1]);

export const TIMEZONES = ["Asia/Riyadh", "Asia/Dubai", "Asia/Kuwait", "Asia/Bahrain", "Asia/Qatar", "Asia/Muscat", "Africa/Cairo", "Asia/Karachi", "Asia/Kolkata", "Asia/Manila", "UTC"];

export const WEEKDAYS: [string, string][] = [["Sunday", "الأحد"], ["Monday", "الإثنين"], ["Tuesday", "الثلاثاء"], ["Wednesday", "الأربعاء"], ["Thursday", "الخميس"], ["Friday", "الجمعة"], ["Saturday", "السبت"]];
export const WEEKDAYS_SHORT: [string, string][] = [["Sun", "أحد"], ["Mon", "إثنين"], ["Tue", "ثلاثاء"], ["Wed", "أربعاء"], ["Thu", "خميس"], ["Fri", "جمعة"], ["Sat", "سبت"]];

/** "HH:mm" of an ISO timestamp, or "—". */
export const hm = (iso: string | null | undefined) => (iso ? iso.slice(11, 16) : "—");
/** Minutes → "7h 05m". */
export function durText(min: number, L: Lx): string {
  if (!min) return "—";
  const h = Math.floor(min / 60), m = min % 60;
  return L(`${h}h ${String(m).padStart(2, "0")}m`, `${h}س ${String(m).padStart(2, "0")}د`);
}
